
import { computed, defineComponent, PropType, reactive, onBeforeMount, onMounted } from 'vue'

// @components
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import ColorPicker from '@/common/components/UI/ColorPicker/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { ExpressZone, CreateExpressZonePayload, UpdateExpressZonePayload, ExpressZoneType } from '@/new-services/types/sectors'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    name: 'new-express-zone-form',
    components: {
        NewInput,
        NewSelect,
        ColorPicker,
        NewButton,
    },
    props: {
        dispatcherSectors: {
            type: Array as PropType<DispatcherSector[]>,
            default: () => [],
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<ExpressZone>,
            default: null,
        },
        isPaintStarted: {
            type: Boolean,
            default: false,
        },
        isSectorAdded: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'start-paint', 'create', 'update', 'change-color'],
    setup(props, { emit }) {
        const generateRandomHex = (): string => {
            return `#${Math.floor(Math.random() * 16777215).toString(16)}`
        }

        const state = reactive({
            name: null as string | null,
            dispatcherSectorId: null as number | null,
            typeId: null as number | null,
            color: generateRandomHex(),
        })

        const types_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as ExpressZoneType[]
        })

        const state_form$ = useVuelidate(
            {
                name: { required },
                dispatcherSectorId: { required },
                typeId: { required },
                color: { required },
            },
            state
        )

        const loadExpressZoneTypes = async () => {
            try {
                types_state.is_loading = true

                const response = await dispatcherSectorsAPI.getExpressZoneTypes({ page: 1, limit: 9999 })
                types_state.options = response.data.data

                types_state.is_failed = false
            } catch (error) {
                console.error(error)
                types_state.is_failed = true
            } finally {
                types_state.is_loading = false
            }
        }

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const onStartPaint = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('start-paint', {
                color: state.color,
                name: state.name,
            })
        }

        const emitCreate = () => {
            const payload: Omit<CreateExpressZonePayload, 'coordinates'> = {
                name: state.name as string,
                color: state.color as string,
                typeId: state.typeId as number,
                dispatcherSectorId: state.dispatcherSectorId as number,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            const payload: Omit<UpdateExpressZonePayload, 'coordinates'> = {
                name: state.name as string,
                color: state.color as string,
                typeId: state.typeId as number,
                dispatcherSectorId: state.dispatcherSectorId as number,
            }

            emit('update', {
                id: props.initialValue.id,
                payload,
            })
        }

        const onSubmit = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                emitCreate()
            }

            if (props.isUpdate) {
                emitUpdate()
            }
        }

        const onChangeColor = (color: string) => {
            if (props.loading) return
            emit('change-color', color)
        }

        onMounted(() => {
            loadExpressZoneTypes()
        })

        onBeforeMount(() => {
            if (props.isUpdate && props.initialValue) {
                state.color = props.initialValue.color
                state.name = props.initialValue.name
                state.dispatcherSectorId = props.initialValue.dispatcherSectorId
                state.typeId = props.initialValue.type.id
            }
        })

        const modalTitle = computed(() => {
            if (props.isCreate) {
                return 'Добавление экспресс зоны'
            }

            if (props.isUpdate) {
                return 'Редактирование экспресс зоны'
            }

            return 'modal title'
        })

        const buttonTitle = computed(() => {
            if (props.isUpdate) {
                return 'Сохранить экспресс зону'
            }

            if (props.isCreate) {
                return 'Добавить экспресс зону'
            }

            return 'button title'
        })

        const isRenderingStartPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted || props.isSectorAdded) {
                return false
            }

            return true
        })

        const isRenderingOnPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted && !props.isSectorAdded) {
                return true
            }

            return false
        })

        const isRenderingSubmit = computed(() => {
            if (props.isUpdate) return true

            if (!props.isPaintStarted && props.isSectorAdded) {
                return true
            }

            return false
        })

        return {
            state,
            types_state,
            state_form$,
            //
            loadExpressZoneTypes,
            onCancel,
            onStartPaint,
            onSubmit,
            onChangeColor,
            //
            modalTitle,
            buttonTitle,
            isRenderingStartPaint,
            isRenderingOnPaint,
            isRenderingSubmit,
        }
    },
})
